import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/pages/home/components/Footer";
import SEOHead from "@/components/base/SEOHead";
import { categories } from "@/mocks/categories";
import { articleList } from "@/mocks/article-data";

function getSiteUrl(): string {
  try {
    return import.meta.env.VITE_SITE_URL || "https://superbuy.study";
  } catch {
    return "https://superbuy.study";
  }
}

const guideSteps = [
  {
    step: "01",
    title: "Create Your Superbuy Account",
    desc: "Sign up for a free Superbuy account using your email address. Fill in your shipping address accurately — this is essential for international delivery.",
    icon: "ri-user-add-line",
  },
  {
    step: "02",
    title: "Browse the Spreadsheet Categories",
    desc: "Explore our 14 product categories above. Each category links to a curated spreadsheet with verified products, quality ratings, and direct purchase links.",
    icon: "ri-grid-line",
  },
  {
    step: "03",
    title: "Find Products You Love",
    desc: "Use the spreadsheet search and filter features to narrow down products by price, quality rating, and category. Click product links to view details on Superbuy.",
    icon: "ri-search-line",
  },
  {
    step: "04",
    title: "Copy Links to Superbuy",
    desc: "Copy the product URL from your browser and paste it into Superbuy's shopping field. The platform loads the product details automatically.",
    icon: "ri-link",
  },
  {
    step: "05",
    title: "Review QC Photos",
    desc: "Once your item arrives at the Superbuy warehouse, review the Quality Control photos carefully. Check sizes, colors, and overall quality before approving shipping.",
    icon: "ri-camera-line",
  },
  {
    step: "06",
    title: "Ship to Your Door",
    desc: "Choose your preferred shipping method and carrier. Build a haul by combining multiple items to save on international shipping costs.",
    icon: "ri-ship-line",
  },
];

const howToJsonLd = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  name: "How to Shop with Superbuy Spreadsheets",
  description: "A complete step-by-step guide that walks you through the entire Superbuy shopping process — from creating your account to receiving your package at your doorstep.",
  step: guideSteps.map((s, i) => ({
    "@type": "HowToStep",
    position: i + 1,
    name: s.title,
    text: s.desc,
  })),
};

export default function ShoppingGuidePage() {
  return (
    <>
      <SEOHead
        title="Superbuy Shopping Guide: How to Shop with Spreadsheets | Superbuy Study"
        description="A complete step-by-step guide that walks you through the entire Superbuy shopping process — from creating your account to receiving your package at your doorstep. Learn how to use spreadsheets to find the best products."
        keywords="superbuy shopping guide, how to use superbuy, superbuy spreadsheet tutorial, superbuy step by step, superbuy buying guide"
        canonicalPath="/shopping-guide"
        ogType="article"
        ogImage="https://readdy.ai/api/search-image?query=Modern%20minimalist%20workspace%20desk%20with%20open%20laptop%20displaying%20spreadsheet%20data%20tables%20and%20charts%20surrounded%20by%20fashion%20items%20sneakers%20hoodie%20accessories%20warm%20natural%20daylight%20clean%20cream%20and%20beige%20palette%20professional%20editorial%20photography%20soft%20shadows&width=1200&height=630&seq=og-shopping-guide-v1&orientation=landscape"
        jsonLd={howToJsonLd}
      />
      <Navbar />
      <main className="pt-24 pb-16 bg-background-50">
        {/* Hero */}
        <section className="max-w-4xl mx-auto px-6 md:px-10 text-center mb-14">
          <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium tracking-wide uppercase mb-4">
            Shopping Guide
          </span>
          <h1 className="text-3xl md:text-5xl font-heading font-semibold text-foreground-950 mb-4">
            How to Shop with Superbuy Spreadsheets
          </h1>
          <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto leading-relaxed">
            A complete step-by-step guide that walks you through the entire Superbuy shopping process — from account creation to receiving your package at your doorstep.
          </p>
        </section>

        {/* Steps */}
        <section className="max-w-4xl mx-auto px-6 md:px-10 mb-16">
          <div className="space-y-4">
            {guideSteps.map((step, idx) => (
              <div
                key={idx}
                className="flex items-start gap-4 md:gap-6 bg-white rounded-xl p-5 md:p-6 border border-background-200/70 hover:border-primary-200 transition-colors"
              >
                <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 shrink-0">
                  <i className={`${step.icon} text-lg md:text-xl`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    <span className="text-xs font-bold text-primary-500 tracking-wider">{step.step}</span>
                    <h3 className="text-base md:text-lg font-heading font-semibold text-foreground-950">{step.title}</h3>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Category Quick Links */}
        <section className="max-w-6xl mx-auto px-6 md:px-10 mb-16">
          <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 text-center mb-8">
            Browse Product Categories
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-2 md:gap-3">
            {categories.map((cat) => (
              <a
                key={cat.id}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-white border border-background-200/70 text-xs md:text-sm font-medium text-foreground-700 hover:border-primary-200 hover:text-primary-700 transition-colors cursor-pointer"
              >
                <span className="w-6 h-6 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 shrink-0">
                  <i className={`${cat.icon} text-xs`}></i>
                </span>
                <span className="truncate">{cat.name}</span>
              </a>
            ))}
          </div>
        </section>

        {/* Article Recommendations */}
        <section className="max-w-6xl mx-auto px-6 md:px-10 mb-16">
          <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 text-center mb-8">
            Recommended Reading
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {articleList.slice(0, 6).map((article) => (
              <Link
                key={article.slug}
                to={`/article/${article.slug}`}
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                className="flex items-start gap-3 bg-white rounded-xl p-4 border border-background-200/70 hover:border-primary-200 transition-colors cursor-pointer group"
              >
                <div className="w-14 h-14 rounded-lg overflow-hidden bg-background-100 shrink-0">
                  <img src={article.imageUrl} alt={article.title} className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="min-w-0">
                  <span className="text-[10px] font-medium text-primary-600">{article.category}</span>
                  <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug mt-0.5">
                    {article.title}
                  </h3>
                  <span className="text-[10px] text-foreground-400">{article.readTime}</span>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-3xl mx-auto px-6 md:px-10 text-center">
          <div className="bg-white rounded-2xl p-8 md:p-10 border border-background-200/70">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-3">
              Ready to Start Shopping?
            </h2>
            <p className="text-sm text-foreground-600 mb-6 max-w-md mx-auto">
              Explore thousands of verified products across all categories. Start with our curated spreadsheets and shop with confidence.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-shopping-cart-2-line"></i>
                Superbuy Spreadsheet
              </a>
              <Link
                to="/"
                className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 rounded-full border border-primary-300 text-primary-700 font-semibold text-sm hover:bg-primary-50 transition-colors cursor-pointer"
              >
                <i className="ri-home-4-line"></i>
                Back to Home
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}